/**
 * Fantasy Console 0 - Auth Error Utilities
 *
 * Maps Supabase auth error messages to user-friendly text.
 */

/**
 * Convert Supabase auth errors to user-friendly messages
 */
export function getAuthErrorMessage(error: unknown): string {
  if (!error) {
    return 'An unknown error occurred'
  }

  // Handle Error objects
  const message = error instanceof Error ? error.message : String(error)

  // Map common Supabase error messages to friendly text
  const errorMappings: Record<string, string> = {
    'User already registered': 'An account with this email already exists',
    'Invalid login credentials': 'Invalid email or password',
    'Email not confirmed': 'Please check your email to confirm your account',
    'Password should be at least 6 characters':
      'Password must be at least 6 characters',
    'Unable to validate email address: invalid format':
      'Please enter a valid email address',
    'Signup requires a valid password': 'Please enter a password',
    'Anonymous sign-ins are disabled':
      'Please sign in with your email and password',
    'Email rate limit exceeded': 'Too many attempts. Please try again later',
    'For security purposes, you can only request this once every 60 seconds':
      'Please wait before trying again',
    'New password should be different from the old password':
      'New password must be different from your current password',
  }

  // Check for exact matches
  if (errorMappings[message]) {
    return errorMappings[message]
  }

  // Check for partial matches
  for (const [key, value] of Object.entries(errorMappings)) {
    if (message.toLowerCase().includes(key.toLowerCase())) {
      return value
    }
  }

  // Return original message if no mapping found
  return message
}

/**
 * Format time ago for display
 * @param date - ISO date string or Date object
 * @returns Human-readable time ago string
 */
export function formatTimeAgo(date: string | Date): string {
  const now = new Date()
  const then = date instanceof Date ? date : new Date(date)
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (seconds < 60) {
    return 'just now'
  }

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes}m ago`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours}h ago`
  }

  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
